import {
  HeadContent,
  Link,
  Scripts,
  createRootRouteWithContext,
} from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowUpRight,
  BookOpen,
  Compass,
  Gamepad2,
  Home,
  Mail,
} from 'lucide-react'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { TanStackDevtools } from '@tanstack/react-devtools'
// Preload the normal hero fonts; italic is fetched only on pages that use it.
import frauncesUrl from '@fontsource-variable/fraunces/files/fraunces-latin-opsz-normal.woff2?url'
import geistUrl from '@fontsource-variable/geist/files/geist-latin-wght-normal.woff2?url'

import TanStackQueryDevtools from '../integrations/tanstack-query/devtools'
import { portfolioData } from '../data/portfolio'

import Navigation from '../components/Navigation'
import Footer from '../components/Footer'
import PreviewCursor from '../components/preview/PreviewCursor'

import appCss from '../styles.css?url'
import { isMissingPage, pageMarkdownHref } from '../lib/pageDiscovery'
import { pageBreadcrumb } from '../lib/breadcrumbs'

export const Route = createRootRouteWithContext<Record<string, never>>()({
  head: ({ matches }) => {
    const page = matches[matches.length - 1]
    const missingPage = isMissingPage(page)
    const markdownHref = pageMarkdownHref(page)
    const twitterSocial = portfolioData.personal.socials.find(
      (s) => s.platform === 'Twitter',
    )
    const twitterHandle = twitterSocial
      ? twitterSocial.url.split('/').pop()
      : 'suriyaprakash'

    const baseUrl = 'https://www.suriyaprakash.in'
    const ogImageUrl = `${baseUrl}/og/default.png`
    const title = missingPage
      ? '404 — Page Not Found | Suriyaprakash Somu'
      : portfolioData.personal.seoTitle
    const description = missingPage
      ? 'The requested page does not exist. Explore the portfolio, case studies and engineering articles.'
      : portfolioData.personal.seoDescription

    return {
      meta: [
        {
          title,
        },
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        {
          name: 'title',
          content: title,
        },
        { name: 'description', content: description },
        { name: 'author', content: portfolioData.personal.name },
        {
          name: 'robots',
          content: missingPage
            ? 'noindex, follow'
            : 'index, follow, max-image-preview:large',
        },
        { name: 'theme-color', content: '#1f4d3a' },
        {
          name: 'apple-mobile-web-app-capable',
          content: 'yes',
        },
        {
          name: 'apple-mobile-web-app-status-bar-style',
          content: 'black-translucent',
        },
        {
          name: 'keywords',
          content: [
            ...new Set([
              'Full Stack Developer',
              'Business Website Development',
              'Custom Web Application Development',
              'Full Stack Developer Tamil Nadu',
              'Full Stack Developer Erode',
              'React Developer',
              'Node.js Developer',
              'TypeScript Developer',
              'ERP Developer',
              'RBAC Developer',
              'Role Based Access Control Developer',
              'Schema Based Forms Developer',
              'Dynamic Query Builder Developer',
              'Next.js Developer',
              'Fastify Developer',
              'PostgreSQL Developer',
              'MySQL Developer',
              'Suriyaprakash',
              'Suriya Prakash',
              'Suriyaprakash Somu',
              ...portfolioData.capabilities.flatMap((c) => c.skills),
              ...portfolioData.works.flatMap((work) => work.stack),
            ]),
          ].join(', '),
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: description,
        },
        { property: 'og:locale', content: 'en_IN' },
        { property: 'og:type', content: 'website' },
        { property: 'og:image', content: ogImageUrl },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        {
          property: 'og:image:alt',
          content:
            'Suriyaprakash Somu — full-stack developer, React and Node.js',
        },
        { property: 'og:site_name', content: portfolioData.personal.name },
        { name: 'twitter:card', content: 'summary_large_image' },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:description',
          content: description,
        },
        { name: 'twitter:image', content: ogImageUrl },
        {
          name: 'twitter:image:alt',
          content:
            'Suriyaprakash Somu — full-stack developer, React and Node.js',
        },
        { name: 'twitter:creator', content: `@${twitterHandle}` },
        { name: 'twitter:site', content: `@${twitterHandle}` },
      ],
      links: [
        ...(markdownHref
          ? [
              {
                rel: 'alternate',
                type: 'text/markdown',
                title: 'Markdown version',
                href: markdownHref,
              },
            ]
          : []),
        {
          rel: 'preload',
          href: frauncesUrl,
          as: 'font',
          type: 'font/woff2',
          crossOrigin: 'anonymous',
        },
        {
          rel: 'preload',
          href: geistUrl,
          as: 'font',
          type: 'font/woff2',
          crossOrigin: 'anonymous',
        },
        { rel: 'stylesheet', href: appCss },
      ],
      scripts: [
        ...pageBreadcrumb(page?.pathname ?? '/'),
        ...(import.meta.env.PROD
          ? [
              {
                tag: 'script',
                children:
                  "window.dataLayer = window.dataLayer || []; window.gtag = window.gtag || function(){window.dataLayer.push(arguments);}; window.addEventListener('load', function(){var loaded = false; var loadAnalytics = function(){if(loaded) return; loaded = true; ['pointerdown','keydown','scroll'].forEach(function(event){window.removeEventListener(event, loadAnalytics);}); var script = document.createElement('script'); script.async = true; script.src = 'https://www.googletagmanager.com/gtag/js?id=G-TL0HS2FW2F'; script.onload = function(){window.gtag('js', new Date()); window.gtag('config', 'G-TL0HS2FW2F');}; document.head.appendChild(script);}; ['pointerdown','keydown','scroll'].forEach(function(event){window.addEventListener(event, loadAnalytics, {once:true, passive:true});}); window.setTimeout(loadAnalytics, 15000);});",
              },
            ]
          : []),
      ],
    }
  },

  notFoundComponent: NotFound,
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-cream-50 focus:px-4 focus:py-3 focus:text-ink"
        >
          Skip to content
        </a>
        <PreviewCursor />
        <Navigation />
        <main id="main-content" tabIndex={-1} className="min-h-screen">
          {children}
        </main>
        <Footer />
        {import.meta.env.DEV ? (
          <TanStackDevtools
            config={{
              position: 'bottom-right',
            }}
            plugins={[
              {
                name: 'Tanstack Router',
                render: <TanStackRouterDevtoolsPanel />,
              },
              TanStackQueryDevtools,
            ]}
          />
        ) : null}
        <Scripts />
      </body>
    </html>
  )
}

function NotFound() {
  const quickLinks = [
    {
      to: '/' as const,
      title: 'Home',
      description: 'Return to the portfolio landing page.',
      icon: Home,
    },
    {
      to: '/playground' as const,
      title: 'Playground',
      description: 'Explore the DataTable and SchemaForm demos.',
      icon: Gamepad2,
    },
    {
      to: '/blog' as const,
      title: 'Blog',
      description: 'Read notes on full-stack systems and architecture.',
      icon: BookOpen,
    },
  ]

  return (
    <section className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(172,196,173,0.5),transparent_34rem),linear-gradient(180deg,#fbf8f1_0%,#f4efe3_55%,#fbf8f1_100%)] px-5 pb-24 pt-28 sm:px-6 sm:pt-36 md:pb-28">
      <div className="absolute inset-0 subtle-grid opacity-40" />
      <div className="container-custom relative">
        <div className="mx-auto max-w-5xl overflow-hidden rounded-[2rem] border border-line/80 bg-card/80 shadow-[0_28px_90px_rgba(19,32,27,0.1)] backdrop-blur sm:rounded-[2.5rem]">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
            <div className="bg-forest-strong p-6 text-white sm:p-8 md:p-10 lg:p-12">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-sage-100 ring-1 ring-white/15 sm:h-16 sm:w-16">
                <Compass className="h-7 w-7 sm:h-8 sm:w-8" />
              </div>
              <p className="mt-8 text-xs font-bold uppercase tracking-[0.24em] text-sage-200">
                404 / Page not found
              </p>
              <h1 className="mt-4 font-heading text-6xl leading-[0.9] tracking-[-0.045em] text-white sm:text-7xl md:text-8xl">
                This route drifted off map.
              </h1>
              <p className="mt-6 max-w-xl text-sm leading-7 text-cream-50/85 sm:text-base sm:leading-8">
                The page may have moved, the link may be outdated, or the URL
                might have a typo. Use one of the paths below to get back to the
                useful parts of the site.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/"
                  className="focus-ring inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-bold text-forest transition-colors hover:bg-sage-100 active:scale-95"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back home
                </Link>
                <a
                  href={`mailto:${portfolioData.personal.email}`}
                  className="focus-ring inline-flex items-center justify-center rounded-full border border-white/20 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10 active:scale-95"
                >
                  Report broken link
                  <Mail className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="p-5 sm:p-7 md:p-10 lg:p-12">
              <div className="rounded-[1.5rem] border border-line bg-cream-50/70 p-4 sm:p-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-forest sm:text-xs">
                  Suggested paths
                </p>
                <div className="mt-4 grid gap-3">
                  {quickLinks.map((item) => {
                    const Icon = item.icon
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="focus-ring group flex items-center justify-between gap-4 rounded-2xl border border-line bg-card p-4 text-left shadow-sm transition-colors hover:border-forest/30 hover:bg-sage-50"
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-forest text-white">
                            <Icon className="h-5 w-5" />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-extrabold text-ink sm:text-base">
                              {item.title}
                            </span>
                            <span className="mt-0.5 block text-xs leading-5 text-ink-soft sm:text-sm">
                              {item.description}
                            </span>
                          </span>
                        </span>
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-forest transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    )
                  })}
                </div>
              </div>

              <div className="mt-5 rounded-[1.5rem] border border-line bg-card p-4 sm:p-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted sm:text-xs">
                  Looking for component demos?
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <Link
                    to="/playground/datatable"
                    className="rounded-full border border-line px-4 py-2 text-sm font-bold text-ink transition-colors hover:border-forest/30 hover:bg-sage-50 hover:text-forest"
                  >
                    DataTable
                  </Link>
                  <Link
                    to="/playground/schemaform"
                    className="rounded-full border border-line px-4 py-2 text-sm font-bold text-ink transition-colors hover:border-forest/30 hover:bg-sage-50 hover:text-forest"
                  >
                    SchemaForm
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
